/**
 * Which licences the session's BRANCH holds, and whether a left-menu module may show for it.
 * Fetched at LOGIN, as soon as the login answers with an `ATOKEN` (`loadBranchLicencesOnLogin`),
 * into `businessStore.branchLicences`. Read by both apps' licence preference modal (the Kiosk
 * licence is offered only when the branch holds it, `isLicenceOfferedForBranch`) and LeftMenu
 * (`useBranchLicences`, beside the assigned-licence and role checks in `setNavigationMenu`).
 *
 * Why it is needed: a module row's `licences` list says under which ASSIGNED licences it shows,
 * and `RHP` (Rista Support) is in every row. So a support login saw every module, the Kiosk
 * included, whether or not the branch had bought it. The branch's own licences are the answer
 * the menu never asked for.
 *
 * Source: `POST branch/filter?includeLicences=true` (rista-be `branch_controller.filterBranches`),
 * made by the business saga (`redux-store/saga/business`, `fetchBranchLicencesFunc`) on the
 * `getBranchLicences` action, which also stores the answer. Only with `includeLicences` does each
 * branch carry `availableLicences`, and the backend fetches those one branch at a time, so the body
 * names this branch alone (`branchIds`, `branch_service.filterBranches`) rather than asking for the
 * whole business.
 */
import { useSelector } from "react-redux";

import store from "../../redux-store";
import { getBranchLicences } from "../../redux-store/slices/BusinessSlice";
import { fetchCommonData } from "../../redux-store/slices/common";
import { ENDPOINT } from "../../config/endpoints";
import { METHODS } from "../../data/constants";
import { LICENCE_CODES } from "./menuItemsData";

/** A branch's licence codes, e.g. `["SEP", "POO", "INV", "KSK"]`. */
export type BranchLicences = readonly string[];

interface SessionBranchState {
  businessStore?: {
    businessData?: { branchId?: string };
    branchLicences?: { branchId: string; licences: string[] } | null;
  };
}

/**
 * May a menu module whose row lists `moduleLicences` show for a branch holding `branchLicences`?
 * Yes when the branch holds at least one of them.
 *
 * `null` (not known: offline, not fetched yet, or the request failed) and an EMPTY list both
 * leave the menu as it was. The empty list follows the backend's own reading in
 * `preferences_controller.getCurrentLicenceInfo`, which limits licences to the branch's only when
 * the branch has any configured; a branch with none would otherwise lose its whole menu.
 */
export function isModuleLicensedForBranch(
  moduleLicences: readonly string[] | undefined,
  branchLicences: BranchLicences | null,
): boolean {
  if (!branchLicences || branchLicences.length === 0) return true;
  if (!moduleLicences?.length) return true;
  return moduleLicences.some((code) => branchLicences.includes(code));
}

/**
 * One request through `fetchCommonData`, the generic REST action both apps' `commonSaga` runs,
 * resolving with the answer's body. `errorCallback` makes the saga call back on a failure too
 * (with the error's body, or nothing), so a failed request still resolves and nothing waits on it
 * forever. No loader: mobile's saga forwards `hideLoader`, web's interceptor reads
 * `skipGlobalLoader`, so both are sent.
 */
function requestCommonData(method: string, url: string, body?: unknown): Promise<unknown> {
  return new Promise((resolve) => {
    store.dispatch(
      fetchCommonData({
        method,
        url,
        body,
        callback: resolve,
        errorCallback: true,
        hideLoader: true,
        skipGlobalLoader: true,
      }),
    );
  });
}

/**
 * The branch's `availableLicences`, or `null` when the backend did not say. The business saga
 * makes the request and stores the answer in `businessStore.branchLicences`; it calls back on a
 * failure too, so this always settles.
 */
export function fetchBranchLicences(branchId: string): Promise<BranchLicences | null> {
  return new Promise((resolve) => {
    store.dispatch(getBranchLicences({ branchId, callback: resolve }));
  });
}

const selectSessionBranchId = (state: SessionBranchState) => state.businessStore?.businessData?.branchId;

/** The stored licences, but only while they belong to the session's current branch. */
export function selectBranchLicences(state: SessionBranchState): BranchLicences | null {
  const entry = state.businessStore?.branchLicences;
  const branchId = selectSessionBranchId(state);
  return entry && branchId && entry.branchId === branchId ? entry.licences : null;
}

/**
 * The branch this login is for. The store knows it on any device that has loaded its business
 * data before (`businessStore` is persisted). A FRESH device does not: neither the registration
 * answer nor the login answer carries a `branchId`, and the licence preference modal opens before
 * the business data is loaded. Then it is read from `preferences/business/detail`, a route with no
 * module gate, so it answers before a licence is chosen.
 */
function resolveSessionBranchId(): Promise<string | undefined> {
  const known = selectSessionBranchId(store.getState() as SessionBranchState);
  if (known) return Promise.resolve(known);
  return requestCommonData(METHODS.GET, ENDPOINT.COMMON.getBusinessData()).then((business) => {
    const branchId = (business as { branchId?: unknown } | undefined)?.branchId;
    return typeof branchId === "string" ? branchId : undefined;
  });
}

/** The load in flight, shared by every caller until it settles. */
let pendingLoad: Promise<BranchLicences | null> | null = null;

/**
 * Called as soon as a login answers with an `ATOKEN` (both platforms' login sagas), and again from
 * `getUserDataOnLogin`, which the support logins go through; a second call while the first is in
 * flight joins it. Fetches the branch's licences (the business saga stores them in the persisted
 * `businessStore.branchLicences`) and resolves with them, or with `null` when the backend did not say.
 */
export function loadBranchLicencesOnLogin(): Promise<BranchLicences | null> {
  if (pendingLoad) return pendingLoad;
  const load = resolveSessionBranchId()
    .then((branchId) => {
      if (!branchId) return null;
      return fetchBranchLicences(branchId);
    })
    .finally(() => {
      pendingLoad = null;
    });
  pendingLoad = load;
  return load;
}

/**
 * The branch's licences for a screen that must wait for them (the licence preference modal): the
 * load in flight, else what this login stored, else a fresh load.
 */
export function ensureBranchLicences(): Promise<BranchLicences | null> {
  if (pendingLoad) return pendingLoad;
  const state = store.getState() as SessionBranchState;
  const entry = state.businessStore?.branchLicences;
  const branchId = selectSessionBranchId(state);
  // with no branch in the store yet, the entry is the one a fresh device's login just resolved
  if (entry && (!branchId || entry.branchId === branchId)) return Promise.resolve(entry.licences);
  return loadBranchLicencesOnLogin();
}

/**
 * May the licence preference modal offer `licenceCode`? The Kiosk licence only when the branch
 * holds it, and never while that is unknown. Every other licence as before: `preferences/licence`
 * already limits the list to the branch's licences, and it adds Analytics (ALT) on purpose, which
 * the branch list does not name, so filtering the rest by it would drop that.
 */
export function isLicenceOfferedForBranch(licenceCode: string, branchLicences: BranchLicences | null): boolean {
  if (licenceCode !== LICENCE_CODES.KSK) return true;
  return !!branchLicences?.includes(LICENCE_CODES.KSK);
}

/**
 * The session branch's licences for the menu, or `null` when they are not to be applied. The
 * menus pass `!isOffline`: offline the menu keeps today's behaviour.
 */
export function useBranchLicences(enabled: boolean): BranchLicences | null {
  const licences = useSelector(selectBranchLicences);
  return enabled ? licences : null;
}
